import * as React from 'react';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Body} from 'shells/typography/body';

import type {
    GetWorkOsInformation_workOs_sso as Sso,
    GetWorkOsInformation_workOs_domainInfo as DomainInfo,
    User,
} from 'nutshell-graphql-types';

import {SsoConfigure, SsoExemptUsers, SsoRequire, SsoDomains, SsoFeatureGateModal} from '..';

import {
    useInitiateSsoSetup,
    useUpdateSsoSettings,
    useTeardownSsoForInstance,
} from '../graphql/hooks';
import {
    formatUserIdsList,
    formatUserList,
    isUserExemptFromSso,
    hasVerifiedDomain,
} from '../helpers';
import type {FormattedOption} from '../helpers';

type Props = {
    sso?: Sso;
    organizationId?: string | null | undefined;
    domainInfo?: DomainInfo;
    isLoading: boolean;
    user: User | null | undefined;
};

export function SsoSettingsSection(props: Props) {
    const {sso, domainInfo} = props;

    const [isFeatureGateModalOpen, setIsFeatureGateModalOpen] = React.useState<boolean>(false);
    const [isRequired, setIsRequired] = React.useState<boolean>(sso ? sso.isRequired : false);
    const [isRequiredError, setIsRequiredError] = React.useState<string | null>(null);
    const [exemptUsersError, setExemptUsersError] = React.useState<string | null>(null);
    const [exemptFromSsoUserIds, setExemptFromSsoUserIds] = React.useState<string[]>(
        // @ts-expect-error
        sso ? formatUserIdsList(sso.exemptUsers) : []
    );
    const [formattedNonExemptUsers, setFormattedNonExemptUsers] = React.useState<FormattedOption[]>(
        sso ? formatUserList(sso.nonExemptUsers) : []
    );
    const [formattedExemptFromSsoUsers, setFormattedExemptFromSsoUsers] = React.useState<
        FormattedOption[]
    >(sso ? formatUserList(sso.exemptUsers) : []);
    const [hasAtLeastOneVerifiedDomain, setHasAtLeastOneVerifiedDomain] = React.useState<boolean>(
        domainInfo ? hasVerifiedDomain(domainInfo) : false
    );

    const {initiateSsoSetup} = useInitiateSsoSetup();
    const {updateSsoSettings} = useUpdateSsoSettings();
    const {teardownSsoForInstance} = useTeardownSsoForInstance();

    let canDisableSso = false;
    if (props.user) {
        const sessionUserId = (props.user && props.user.id) || '';
        canDisableSso = isUserExemptFromSso(sessionUserId, exemptFromSsoUserIds);
    }

    // Need to update variables dependent on sso and domainInfo once the component is done loading
    React.useEffect(() => {
        if (sso && domainInfo) {
            setIsRequired(sso.isRequired);
            // @ts-expect-error
            setExemptFromSsoUserIds(formatUserIdsList(sso.exemptUsers));
            setFormattedNonExemptUsers(formatUserList(sso.nonExemptUsers));
            setFormattedExemptFromSsoUsers(formatUserList(sso.exemptUsers));
            setHasAtLeastOneVerifiedDomain(hasVerifiedDomain(domainInfo));
        }
    }, [sso, domainInfo]);

    const handleSetupSso = () => {
        initiateSsoSetup().then((result) => {
            if (
                result.data &&
                result.data.initiateSsoSetup &&
                result.data.initiateSsoSetup.redirectUrl
            ) {
                window.open(result.data.initiateSsoSetup.redirectUrl, '_blank');
            }
        });
    };

    const handleManageSso = () => {
        // @ts-expect-error
        window.open(sso ? sso.adminPortal : '', '_blank');
    };

    const handleDisableSso = () => {
        teardownSsoForInstance().then(() => {
            window.location.reload();
        });
    };

    const handleIsRequiredConfirmedChange = () => {
        const newIsRequired = !isRequired;
        updateSsoSettings({
            instanceRequireSso: newIsRequired,
            exemptFromSsoUserIds: exemptFromSsoUserIds,
        })
            .then(() => {
                setIsRequired(newIsRequired);
            })
            .catch(() => {
                setIsRequiredError('Error updating SSO required settings');
                setTimeout(() => {
                    setIsRequiredError(null);
                }, 5000);
            });
    };

    // @ts-expect-error
    const handleAddExemptUser = (selectedUser) => {
        const newExemptFromSsoUserIds = [...exemptFromSsoUserIds, selectedUser.value];
        setExemptUsersError(null);
        updateSsoSettings({
            instanceRequireSso: isRequired,
            exemptFromSsoUserIds: newExemptFromSsoUserIds,
        })
            .then(() => {
                setExemptFromSsoUserIds(newExemptFromSsoUserIds);
                setFormattedNonExemptUsers((prevUsers) =>
                    prevUsers.filter((user) => user.value !== selectedUser.value)
                );
                setFormattedExemptFromSsoUsers((prevUsers) => [...prevUsers, selectedUser]);
            })
            .catch((err) => {
                setExemptUsersError(err.graphQLErrors[0].debugMessage);
                setTimeout(() => {
                    setExemptUsersError(null);
                }, 5000);
            });
    };

    // @ts-expect-error
    const handleRemoveExemptUser = (selectedUser) => {
        const newExemptFromSsoUserIds = exemptFromSsoUserIds.filter((userId) => {
            return userId !== selectedUser.value;
        });

        updateSsoSettings({
            instanceRequireSso: isRequired,
            exemptFromSsoUserIds: newExemptFromSsoUserIds,
        })
            .then(() => {
                setExemptFromSsoUserIds(newExemptFromSsoUserIds);
                setFormattedExemptFromSsoUsers((prevUsers) =>
                    prevUsers.filter((user) => user.value !== selectedUser.value)
                );
                setFormattedNonExemptUsers((prevUsers) => {
                    const newUsers = [
                        ...prevUsers,
                        {value: selectedUser.value, label: selectedUser.label},
                    ];

                    return newUsers.sort((a, b) => {
                        if (a.label && b.label) {
                            return a.label.localeCompare(b.label);
                        }

                        return 0;
                    });
                });
            })
            .catch((err) => {
                setExemptUsersError(err.graphQLErrors[0].debugMessage);
                setTimeout(() => {
                    setExemptUsersError(null);
                }, 5000);
            });
    };

    if (props.isLoading) {
        return <LoadingPage />;
    }

    if (!sso || !domainInfo) {
        return (
            <Body color='error' size='small' as='div'>
                <div className='pad-16'>Error getting SSO info</div>
            </Body>
        );
    }

    return (
        <div className='pad-16'>
            <StackLegacy spacing={16}>
                <SsoConfigure
                    canSetupSso={sso.canSetup}
                    isConfigured={sso.isConfigured}
                    onManageSso={handleManageSso}
                    onDisableSso={handleDisableSso}
                    onSetupSso={handleSetupSso}
                    canDisableSso={canDisableSso}
                    hasVerifiedDomain={hasAtLeastOneVerifiedDomain}
                    organizationId={props.organizationId}
                    setIsFeatureGateModalOpen={setIsFeatureGateModalOpen}
                />
                {props.organizationId && sso.canSetup && (
                    <StackLegacy spacing={16}>
                        <SsoExemptUsers
                            formattedNonExemptUsers={formattedNonExemptUsers}
                            formattedExemptFromSsoUsers={formattedExemptFromSsoUsers}
                            handleAddExemptUser={handleAddExemptUser}
                            handleRemoveExemptUser={handleRemoveExemptUser}
                            exemptUsersError={exemptUsersError}
                        />
                        <SsoDomains domainInfo={domainInfo} />
                        <SsoRequire
                            isRequired={isRequired}
                            canToggleIsRequired={
                                sso.isConfigured &&
                                hasAtLeastOneVerifiedDomain &&
                                exemptFromSsoUserIds.length > 0
                            }
                            handleIsRequiredConfirmedChange={handleIsRequiredConfirmedChange}
                            isRequiredError={isRequiredError}
                        />
                    </StackLegacy>
                )}
            </StackLegacy>
            {isFeatureGateModalOpen && (
                <SsoFeatureGateModal
                    onClose={() => setIsFeatureGateModalOpen(false)}
                    isOpen={isFeatureGateModalOpen}
                />
            )}
        </div>
    );
}
